import * as React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Badge,
    Button,
    CardBody,
    CardCheck,
    CardCheckbox,
    CardRadio,
    CheckboxGroup,
    Description,
    Group,
    Heading,
    LoadingIndicator,
    RadioGroup,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import { DocumentationLink, PromptBox } from '../../Components';
import { useSetAnalyticsScreen } from '../../navigation';
import { type AIProvider, AIProviderLaunchButton, prompt } from '../Onboarding';

type SiteOnboardingStep =
    | { type: 'source' }
    | { type: 'audience' }
    | { type: 'interests' }
    | { type: 'ai-setup' };

const TOTAL_ONBOARDING_STEPS = 4;

type OnboardingOption<Value extends string> = {
    value: Value;
    icon: React.ComponentType<{ size?: number; className?: string }>;
    title: string;
    description: string;
};

const SOURCE_OPTIONS: Array<OnboardingOption<api.SiteOnboardingSource>> = [
    {
        value: api.SiteOnboardingSource.MarkdownInGit,
        icon: Icon.GitHub,
        title: 'Markdown in a Git repository',
        description: 'Your docs already live in GitHub or GitLab.',
    },
    {
        value: api.SiteOnboardingSource.Import,
        icon: Icon.Download,
        title: 'I have content to import',
        description: 'Bring in a file, a URL, or content from another docs site.',
    },
    {
        value: api.SiteOnboardingSource.Template,
        icon: Icon.Magic,
        title: 'Start with our template',
        description: 'Explore GitBook and add your content later.',
    },
];

const AUDIENCE_OPTIONS: Array<OnboardingOption<api.SiteOnboardingAudience>> = [
    {
        value: api.SiteOnboardingAudience.Public,
        icon: Icon.PublishStatusPublic,
        title: 'Public to the internet',
        description:
            'Publish your docs openly, so anyone in the world — human or agent — can access them.',
    },
    {
        value: api.SiteOnboardingAudience.Mixed,
        icon: Icon.PublishStatusUnlisted,
        title: 'Some public, some protected',
        description: 'Keep some pages open to everyone and gate the rest behind authentication.',
    },
    {
        value: api.SiteOnboardingAudience.Protected,
        icon: Icon.PublishStatusVisitorAuth,
        title: 'Fully protected',
        description:
            'Require authentication for everything — via share links, or by managing auth yourself with a provider like Okta.',
    },
    {
        value: api.SiteOnboardingAudience.Internal,
        icon: Icon.Lock,
        title: 'Internal only',
        description:
            "Restrict access using your company's internal access management, like Google Workspace or Active Directory.",
    },
];

const INTEREST_OPTIONS: Array<OnboardingOption<api.SiteOnboardingInterest>> = [
    {
        value: api.SiteOnboardingInterest.ApiDocs,
        icon: Icon.OpenAPI,
        title: 'Documenting an OpenAPI spec',
        description: 'Interactive, testable API reference docs.',
    },
    {
        value: api.SiteOnboardingInterest.AdaptiveContent,
        icon: Icon.Magic,
        title: 'Adaptive content',
        description: "Show, hide, or adapt content based on who's reading.",
    },
    {
        value: api.SiteOnboardingInterest.ProactiveAgent,
        icon: Icon.AIAgent,
        title: 'Automatic doc improvements',
        description: 'Let GitBook Agent find and fix gaps or outdated pages.',
    },
];

/**
 * Ask the user where their docs are coming from, who can access their site, and
 * what they're interested in, to inform recommendations later. Ends with helping
 * them connect an AI tool. Fully skippable; answers are not required to start
 * using the site.
 *
 * Runs before the organization and site exist, so it collects answers in memory
 * only; the caller is responsible for persisting them once a site has been created.
 */
export function SiteOnboardingScreen<Result>(props: {
    // Called once, as soon as the last question is answered, so the site is ready
    // (or close to it) by the time the user is done setting up their AI tools.
    onCreateSite: (preferences: api.SiteOnboardingPreferences) => Promise<Result | null>;
    onGoToSite: (result: Result) => void;
}) {
    const { onCreateSite, onGoToSite } = props;
    const [step, setStep] = React.useState<SiteOnboardingStep>({ type: 'source' });
    const [source, setSource] = React.useState<api.SiteOnboardingSource | null>(
        api.SiteOnboardingSource.MarkdownInGit
    );
    const [audience, setAudience] = React.useState<api.SiteOnboardingAudience | null>(
        api.SiteOnboardingAudience.Public
    );
    const [interests, setInterests] = React.useState<api.SiteOnboardingInterest[]>([]);
    const [createSiteState, setCreateSiteState] = React.useState<
        { status: 'pending' } | { status: 'error' } | { status: 'done'; result: Result }
    >({ status: 'pending' });

    const submitSource = useEventCallback((skipped: boolean) => {
        if (skipped) {
            setSource(null);
        }
        analytics.logEvent({
            type: 'onboarding_source_select',
            source: skipped ? 'SKIP' : (source ?? undefined),
        });
        setStep({ type: 'audience' });
    });

    const submitAudience = useEventCallback((skipped: boolean) => {
        if (skipped) {
            setAudience(null);
        }
        analytics.logEvent({
            type: 'onboarding_audience_select',
            audience: skipped ? 'SKIP' : (audience ?? undefined),
        });
        setStep({ type: 'interests' });
    });

    const createSite = useEventCallback((interestsOverride?: api.SiteOnboardingInterest[]) => {
        setCreateSiteState({ status: 'pending' });
        void onCreateSite({
            source,
            audience,
            interests: interestsOverride ?? interests,
        }).then((result) => {
            setCreateSiteState(result ? { status: 'done', result } : { status: 'error' });
        });
    });

    const submitInterests = useEventCallback((skipped: boolean) => {
        const submittedInterests = skipped ? [] : interests;
        if (skipped) {
            setInterests(submittedInterests);
        }
        analytics.logEvent({ type: 'onboarding_interests_select', interests: submittedInterests });
        setStep({ type: 'ai-setup' });
        createSite(submittedInterests);
    });

    const goToSite = useEventCallback(() => {
        if (createSiteState.status === 'done') {
            onGoToSite(createSiteState.result);
        }
    });

    useSetAnalyticsScreen({ screen: getAnalyticsScreen(step) }, [step]);

    switch (step.type) {
        case 'source':
            return (
                <SiteOnboardingStepLayout
                    step={getStepNumber(step)}
                    title="Where are your docs today?"
                    description="This helps us recommend the best way to get your content into GitBook."
                    onSkip={() => submitSource(true)}
                    onContinue={() => submitSource(false)}
                    continueLabel="Continue"
                >
                    <div className="flex flex-col gap-10">
                        <RadioGroup
                            aria-label="Where are your docs today?"
                            value={source ?? undefined}
                            onChange={(value) => setSource(value as api.SiteOnboardingSource)}
                        >
                            <SiteOnboardingOptionsList options={SOURCE_OPTIONS} single />
                        </RadioGroup>
                        <Alert
                            kind="default"
                            icon={Icon.GitHub}
                            title="Docs as code, powered by GitHub & GitLab Sync"
                            details={
                                <>
                                    No matter how you choose to get started, you can connect a
                                    GitHub or GitLab repository at any time to keep your docs in
                                    sync in both directions.{' '}
                                    <DocumentationLink path="docs-as-code">
                                        Learn more
                                    </DocumentationLink>
                                    .
                                </>
                            }
                        />
                    </div>
                </SiteOnboardingStepLayout>
            );
        case 'audience':
            return (
                <SiteOnboardingSingleStepContent
                    step={getStepNumber(step)}
                    title="Who should be able to see your docs?"
                    description="GitBook can host your docs in different ways depending on who should have access — you can always change this later."
                    options={AUDIENCE_OPTIONS}
                    selected={audience}
                    onSelectedChange={(value) => setAudience(value)}
                    onBack={() => setStep({ type: 'source' })}
                    onSkip={() => submitAudience(true)}
                    onContinue={() => submitAudience(false)}
                    continueLabel="Continue"
                />
            );
        case 'interests':
            return (
                <SiteOnboardingMultiStepContent
                    step={getStepNumber(step)}
                    title="What else are you interested in?"
                    description="We'll use this to tailor tips and recommendations during your onboarding."
                    options={INTEREST_OPTIONS}
                    selected={interests}
                    onSelectedChange={setInterests}
                    onBack={() => setStep({ type: 'audience' })}
                    onSkip={() => submitInterests(true)}
                    onContinue={() => submitInterests(false)}
                    continueLabel="Create my docs"
                />
            );
        case 'ai-setup':
            return (
                <SiteOnboardingAIToolStep
                    createSiteStatus={createSiteState.status}
                    onGoToSite={goToSite}
                    onRetry={createSite}
                />
            );
        default:
            assertNever(step);
    }
}

function getAnalyticsScreen(
    step: SiteOnboardingStep
):
    | 'site_onboarding_source'
    | 'site_onboarding_audience'
    | 'site_onboarding_interests'
    | 'site_onboarding_ai_setup' {
    switch (step.type) {
        case 'source':
            return 'site_onboarding_source';
        case 'audience':
            return 'site_onboarding_audience';
        case 'interests':
            return 'site_onboarding_interests';
        case 'ai-setup':
            return 'site_onboarding_ai_setup';
        default:
            assertNever(step);
    }
}

function getStepNumber(step: SiteOnboardingStep): number {
    switch (step.type) {
        case 'source':
            return 1;
        case 'audience':
            return 2;
        case 'interests':
            return 3;
        case 'ai-setup':
            return 4;
        default:
            assertNever(step);
    }
}

function SiteOnboardingStepCounter(props: { step: number }) {
    return (
        <Badge
            kind="secondary"
            size="small"
            label={`Step ${props.step}/${TOTAL_ONBOARDING_STEPS}`}
        />
    );
}

function SiteOnboardingMultiStepContent<Value extends string>(props: {
    step: number;
    title: string;
    description: string;
    options: Array<OnboardingOption<Value>>;
    selected: Value[];
    onSelectedChange: (selected: Value[]) => void;
    onBack?: () => void;
    onSkip?: () => void;
    onContinue: (selected: Value[]) => void;
    continueLabel: string;
}) {
    const { selected, onSelectedChange, onContinue, ...rest } = props;

    return (
        <SiteOnboardingStepLayout {...rest} onContinue={() => onContinue(selected)}>
            <CheckboxGroup
                aria-label={props.title}
                value={selected}
                onChange={(value) => onSelectedChange(value as Value[])}
            >
                <SiteOnboardingOptionsList options={props.options} />
            </CheckboxGroup>
        </SiteOnboardingStepLayout>
    );
}

function SiteOnboardingSingleStepContent<Value extends string>(props: {
    step: number;
    title: string;
    description: string;
    options: Array<OnboardingOption<Value>>;
    selected: Value | null;
    onSelectedChange: (selected: Value | null) => void;
    onBack?: () => void;
    onSkip: () => void;
    onContinue: () => void;
    continueLabel: string;
}) {
    const { selected, onSelectedChange, ...rest } = props;

    return (
        <SiteOnboardingStepLayout {...rest}>
            <RadioGroup
                aria-label={props.title}
                value={selected ?? undefined}
                onChange={(value) => onSelectedChange(value as Value)}
            >
                <SiteOnboardingOptionsList options={props.options} single />
            </RadioGroup>
        </SiteOnboardingStepLayout>
    );
}

function SiteOnboardingOptionsList<Value extends string>(props: {
    options: Array<OnboardingOption<Value>>;
    single?: boolean;
}) {
    const { options, single } = props;

    return (
        <div className="flex flex-col gap-3">
            {options.map((option) => {
                const CardComponent = single ? CardRadio : CardCheckbox;
                return (
                    <CardComponent key={option.value} value={option.value}>
                        <CardBody>
                            <div className="flex w-full items-center gap-4">
                                <option.icon size={20} className="shrink-0 text-secondary" />
                                <Group className="min-w-0 flex-1">
                                    <Heading level={5} className="m-0">
                                        {option.title}
                                    </Heading>
                                    <Description>{option.description}</Description>
                                </Group>
                                <CardCheck kind={single ? 'radio' : 'checkbox'} />
                            </div>
                        </CardBody>
                    </CardComponent>
                );
            })}
        </div>
    );
}

function SiteOnboardingStepLayout(props: {
    step: number;
    title: string;
    description: string;
    children: React.ReactNode;
    onBack?: () => void;
    onSkip?: () => void;
    onContinue: () => void;
    continueLabel: string;
}) {
    const { step, title, description, children, onBack, onSkip, onContinue, continueLabel } = props;

    return (
        <div className="flex w-full min-w-0 flex-col gap-8 duration-300 ease-snappy-out animate-in fade-in lg:min-h-[36rem] lg:pt-16">
            <div className="flex w-full flex-col gap-2">
                <SiteOnboardingStepCounter step={step} />
                <h2 className="text-ui-heading-large">{title}</h2>
                <Description>{description}</Description>
            </div>

            {children}

            <div className="mt-auto flex items-center justify-between gap-2">
                {onBack ? (
                    <Button
                        label="Back"
                        kind="blank"
                        leadingIcon={Icon.ArrowLeft}
                        onPress={onBack}
                    />
                ) : (
                    <div />
                )}
                <div className="flex gap-2">
                    {onSkip ? <Button kind="blank" label="Skip" onPress={onSkip} /> : null}
                    <Button
                        kind="primary"
                        label={continueLabel}
                        trailingIcon={Icon.ArrowRight}
                        onPress={onContinue}
                    />
                </div>
            </div>
        </div>
    );
}

function SiteOnboardingAIToolStep(props: {
    createSiteStatus: 'pending' | 'error' | 'done';
    onGoToSite: () => void;
    onRetry: () => void;
}) {
    const { createSiteStatus, onGoToSite, onRetry } = props;

    const trackAIContentCreateIntent = (_sourceType: AIProvider | 'other') => {
        // Not tied to a site yet; nothing to attribute the event to at this point.
    };

    return (
        <div className="flex w-full min-w-0 flex-col gap-8 duration-300 ease-snappy-out animate-in fade-in lg:min-h-[36rem] lg:pt-16">
            <div className="flex w-full flex-col gap-2">
                <SiteOnboardingStepCounter step={4} />
                <h2 className="text-ui-heading-large">Set up your local workflow</h2>
                <Description>
                    GitBook fully supports local workflows — use your favorite AI tool, our app, or
                    our API.
                </Description>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <AIProviderLaunchButton provider="claude" onLaunch={trackAIContentCreateIntent} />
                <AIProviderLaunchButton provider="cursor" onLaunch={trackAIContentCreateIntent} />
                <AIProviderLaunchButton provider="codex" onLaunch={trackAIContentCreateIntent} />
            </div>
            <p className="text-center text-ui-base text-muted">or</p>
            <PromptBox prompt={prompt} onCopy={() => trackAIContentCreateIntent('other')} />

            {createSiteStatus === 'error' ? (
                <Alert
                    kind="danger"
                    title="We couldn't set up your docs"
                    details="Something went wrong while creating your organization. Please try again."
                />
            ) : null}

            <div className="flex items-center justify-end gap-2">
                {createSiteStatus === 'pending' ? (
                    <div className="flex items-center gap-2 text-muted">
                        <LoadingIndicator size="small" />
                        <span className="text-ui-small">Creating your docs</span>
                    </div>
                ) : createSiteStatus === 'done' ? (
                    <div className="flex items-center gap-2 text-success">
                        <Icon.CheckCircle size={16} />
                        <span className="text-ui-small">Your docs are ready</span>
                    </div>
                ) : null}
                {createSiteStatus === 'error' ? (
                    <Button kind="primary" label="Try again" onPress={onRetry} />
                ) : (
                    <Button
                        kind="primary"
                        label="Go to app"
                        disabled={createSiteStatus !== 'done'}
                        onPress={onGoToSite}
                    />
                )}
            </div>
        </div>
    );
}
